// [목적] 댓글 대상 어댑터 기반 범용 댓글 훅 — 목록(무한스크롤) + 작성/수정/삭제 mutation
// [이유] festival-comments Spec D-8/OQ-4 — 기존 useFolioComments 는 folioId·folioSocialClient·queryKey 에 하드결합이라
//         축제 댓글이 같은 UI/로직을 재사용할 수 없었다. 대상별 차이(4개 CRUD 호출 + queryKey + 게스트 intent +
//         신고 targetType)를 어댑터로 주입받아 폴리오·축제가 한 구현을 공유한다.
//         GET .../comments 는 parentId 미지정 시 최상위+대댓글이 평탄하게 반환되므로 트리 구성 없이 평탄 시간순 목록으로 다룬다.
// [사이드 이팩트] create/patch/remove 성공 시 adapter.queryKey prefix 를 invalidate → 해당 대상의 모든 페이지 캐시 재조회.
//                 queryKey 끝의 q 객체({})는 TanStack partialMatchKey 규칙상 어떤 객체와도 부분 일치하므로
//                 q 변형 엔트리도 함께 무효화된다(기존 useFolioComments 의 prefix invalidate 와 동일 효과).
// [수정 시 고려사항] 낙관적 업데이트 없이 invalidate 후 refetch — cursor 무한목록에 낙관적 삽입/삭제를 반영하면 페이지 경계가
//                   어긋날 수 있어 단순화. create 는 입력 객체를 어댑터에 그대로 통과시킨다(TR-9: parentId:null 정규화 등
//                   필드 재구성 금지 — 폴리오 테스트가 addComment(id,{text}) 호출 형태를 단언한다).
//                   adapter 는 렌더마다 새 객체여도 무방(queryKey 값만 캐시 정체성, 함수는 최신 클로저 사용).

import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReportTargetType } from '@gangwoncanvas/types';

import type { AuthIntent } from './useAuthGate.js';

/**
 * [목적] 댓글 대상(폴리오/축제) 어댑터 — 훅과 CommentSection 이 대상별 차이를 이 계약으로만 접근
 * [이유] Spec D-8. TItem 은 목록 아이템 타입(폴리오: FolioComment, 축제: FestivalComment).
 * [사이드 이팩트] 없음(타입 선언만)
 * [수정 시 고려사항] create 의 input 은 그대로 클라이언트에 전달된다. remove/patch 의 commentId 는 대상 내 댓글 id.
 *                   TItem 은 반환 위치에만 쓰이므로 CommentSection 이 요구하는 구조 타입(GenericComment)에 공변 대입 가능.
 */
export interface CommentTargetAdapter<TItem = unknown> {
  /** useInfiniteQuery 캐시 키 — 대상별로 서로 달라야 한다(폴리오 ['folioSocial',...] / 축제 ['festivalComments',...]) */
  queryKey: readonly unknown[];
  /** 게스트 댓글 작성 시도 시 LoginPromptModal 카피 결정용 intent */
  commentIntent: AuthIntent;
  /** ReportDialog 에 전달할 신고 대상 종류 */
  reportTargetType: ReportTargetType;
  list: (opts: { cursor?: string }) => Promise<{ items: TItem[]; nextCursor: string | null }>;
  create: (input: { text: string; parentId?: string }) => Promise<unknown>;
  patch: (commentId: string, text: string) => Promise<unknown>;
  remove: (commentId: string) => Promise<unknown>;
}

export interface UseCommentsResult<TItem> {
  items: TItem[];
  isLoading: boolean;
  error: unknown;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  create: (text: string, parentId?: string) => Promise<unknown>;
  isCreating: boolean;
  patch: (commentId: string, text: string) => Promise<unknown>;
  isPatching: boolean;
  remove: (commentId: string) => Promise<unknown>;
  isRemoving: boolean;
}

/**
 * [목적] 범용 댓글 훅 — 어댑터가 가리키는 대상의 목록 무한스크롤 + 작성/수정/삭제 mutation
 * [이유] CommentSection 이 이 훅 하나로 댓글 CRUD 전체를 처리(대상 무관).
 * [사이드 이팩트] 각 mutation 성공 시 목록 invalidate(전체 재조회) — 최신 상태 보장. staleTime 30s.
 * [수정 시 고려사항] 어댑터의 대상 id(folioId/contentId)는 항상 truthy 라고 가정(호출부가 부모 로딩 완료 후에만 마운트).
 */
export function useComments<TItem>(adapter: CommentTargetAdapter<TItem>): UseCommentsResult<TItem> {
  const qc = useQueryClient();

  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: adapter.queryKey,
      queryFn: ({ pageParam }) => adapter.list({ cursor: pageParam as string | undefined }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 1000 * 30,
      throwOnError: false,
    });

  const items = data?.pages.flatMap((p) => p.items) ?? [];

  function invalidateList(): void {
    void qc.invalidateQueries({ queryKey: adapter.queryKey });
  }

  const createMutation = useMutation({
    mutationFn: (input: { text: string; parentId?: string }) => adapter.create(input),
    onSuccess: invalidateList,
  });

  const patchMutation = useMutation({
    mutationFn: (input: { commentId: string; text: string }) =>
      adapter.patch(input.commentId, input.text),
    onSuccess: invalidateList,
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => adapter.remove(commentId),
    onSuccess: invalidateList,
  });

  return {
    items,
    isLoading,
    error,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
    create: (text, parentId) => createMutation.mutateAsync({ text, parentId }),
    isCreating: createMutation.isPending,
    patch: (commentId, text) => patchMutation.mutateAsync({ commentId, text }),
    isPatching: patchMutation.isPending,
    remove: (commentId) => deleteMutation.mutateAsync(commentId),
    isRemoving: deleteMutation.isPending,
  };
}
